import type { BeforeSendFn, CaptureResult } from 'posthog-js';
import type {
  NormalizedError,
  PreCaptureHook,
  PreCapturePayload,
} from './types';

export const toSafeString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const compactProperties = (properties: Record<string, unknown>): Record<string, unknown> => {
  const compacted: Record<string, unknown> = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (value !== undefined) {
      compacted[key] = value;
    }
  });
  return compacted;
};

export const normalizeError = (error: unknown): NormalizedError => {
  if (error instanceof Error) {
    const errWithMeta = error as Error & { code?: unknown; type?: unknown };
    return {
      name: error.name || 'Error',
      message: error.message || 'Unknown error',
      stack: error.stack,
      code: toSafeString(errWithMeta.code),
      type: toSafeString(errWithMeta.type),
    };
  }

  if (typeof error === 'object' && error !== null) {
    const obj = error as Record<string, unknown>;
    const message =
      toSafeString(obj.message) ||
      toSafeString(obj.error) ||
      toSafeString(obj.details) ||
      'Unknown object error';
    const type = toSafeString(obj.type) || toSafeString(obj.error_type);
    const code = toSafeString(obj.code) || toSafeString(obj.error_code);
    return {
      name: type || 'ObjectError',
      message,
      stack: toSafeString(obj.stack),
      code,
      type,
    };
  }

  return {
    name: 'UnknownError',
    message: toSafeString(error) || 'Unknown error',
  };
};

export const coerceError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  const normalized = normalizeError(error);
  const coerced = new Error(normalized.message);
  coerced.name = normalized.name;
  if (normalized.stack) {
    coerced.stack = normalized.stack;
  }
  return coerced;
};

const defaultPreCapture = (payload: PreCapturePayload): PreCapturePayload => ({
  ...payload,
  properties: compactProperties(payload.properties),
});

export const runPreCapture = (
  payload: PreCapturePayload,
  override?: PreCaptureHook,
): PreCapturePayload | null => {
  const normalized = defaultPreCapture(payload);
  if (!override) return normalized;
  return override(normalized);
};

export const createBrowserBeforeSend = (
  runtime: string,
  override?: BeforeSendFn,
): BeforeSendFn => {
  return (event: CaptureResult | null): CaptureResult | null => {
    if (!event || typeof event !== 'object') return event;
    if (event.event !== '$exception') return event;
    event.properties = compactProperties({
      ...(event.properties || {}),
      environment: runtime,
    });
    if (!override) return event;
    return override(event);
  };
};
